import { Download } from '@mui/icons-material';
import { Box, Grid, Link, Stack, TextFieldProps, Typography } from '@mui/material';
import CutterTooltip from '../common/CutterTooltip';
import React from 'react';
import Carousel from 'react-material-ui-carousel';
import { CarouselProps } from 'react-material-ui-carousel/dist/components/types';
import { ETLLastUpdate, useGetAppContextQuery } from '../redux/appContext';
import { CutterState } from '../types/cutterState';
import { getDurationStringFromDate, getFormatedStringFromDate, getZipFileLink } from "../utils/dateUtils";
import CutterMetricsStack from '../common/CutterMetricsStack';

export type CarouselTextFieldProps = {
	link?: string
} & TextFieldProps;

const CarouselTextField: React.FC<CarouselTextFieldProps> = (props: CarouselTextFieldProps) => {
	const { link, label, defaultValue } = props;
	const valueElement: React.JSX.Element = link !== undefined ? (
		<CutterTooltip arrow title={
			<div style={{ whiteSpace: 'nowrap' }}>
				Download last zip report:<br />
				{`${link}`}
			</div>} placement="top" copyText={link}>
			<Link download href={link} fontSize='0.8rem'>
				<Stack direction="row" alignItems="center">
					{defaultValue as string}&nbsp;<Download sx={{ color: 'text.primary', fontSize: '1em' }} />
				</Stack>
			</Link>
		</CutterTooltip>
	) : (
		<Typography component="span" fontWeight='bold' color='text.primary' fontSize='0.8rem' variant="body2">
			{defaultValue as string}
		</Typography>
	);
	return (
		<Box component="div" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', whiteSpace: 'nowrap', paddingX: 1, paddingY: 0.25 }}>
			<Typography
				component="span"
				fontWeight='light'
				color='text.secondary'
				fontSize='0.8rem'
				variant="body2"
				sx={{ minWidth: 150, flexShrink: 0 }}
			>
				{((label ?? '') === '') ? '#N/A' : label} :
			</Typography>
			{valueElement}
		</Box>
	);
}
export interface CuttersDetailsCarouselProps extends CarouselProps {
	cutter: CutterState;
}

export const CuttersDetailsCarousel: React.FC<CuttersDetailsCarouselProps> = (props: CuttersDetailsCarouselProps) => {
	const { cutter, ...others } = props;
	const appContext: ReturnType<typeof useGetAppContextQuery> = useGetAppContextQuery(undefined, { skip: false });
	const reportZipLink: string | undefined = getZipFileLink(cutter.cutR_ZipFileName, cutter.cutR_ZipStoredOnDisk ?? false);

	let lisaLastUpdate: string = getFormatedStringFromDate(new Date);

	React.useEffect(() => {
		if (appContext.isSuccess) {

			const etlSteps: ETLLastUpdate[] = appContext.data as ETLLastUpdate[];
			const lisaDate: Date = etlSteps?.find(etlStep => etlStep.rank === 3)?.lastUpdate ?? new Date;
			lisaLastUpdate = appContext ? getFormatedStringFromDate(lisaDate) : 'Updating...';
		}

	}, [appContext.status]);

	let children: React.JSX.Element[] = [
		(<Grid key='metrics' >
			<CutterMetricsStack
			commercialVersion={cutter.sofT_Commercial_Version || null}
			SoftwareUpdateIndex={cutter.stats_SoftwareUpdateIndex ?? null}
			ConnectionRate={cutter.stats_ConnectionRate ?? null}
			/></Grid>),
		(<Grid key='counters' >
			<CarouselTextField defaultValue={cutter.sofT_Main_Feature_HostId?.replace('-1001', '')} label={<b>Host Id</b>} />
			<CarouselTextField defaultValue={cutter.cutR_EffectiveCuttingTime?.toFixed(2)} label="EffectiveCuttingTime" />
			<CarouselTextField defaultValue={cutter.cutR_VacuumTime?.toFixed(2)} label="Vacuum time" />
		</Grid>),
		(<Grid key='cutterreport' >
			<CarouselTextField defaultValue={getDurationStringFromDate(cutter.cutR_ZipUploadDate).trim()} label={<b>CutterReport</b>} />
			<CarouselTextField defaultValue={getFormatedStringFromDate(cutter.cutR_ZipCreationDate)} link={reportZipLink} label="Latest Report" />
			<CarouselTextField defaultValue={getFormatedStringFromDate(cutter.cutR_ExtractDate)} label="Information Date" />
		</Grid>),
		(<Grid key='lisa'>
			<CarouselTextField defaultValue={getDurationStringFromDate(cutter.lisA_LastReport)} label={<b>Lisa</b>} />
			<CarouselTextField defaultValue={getFormatedStringFromDate(cutter.lisA_LastReport ?? new Date)} label="Latest Report" />
			<CarouselTextField defaultValue={getFormatedStringFromDate(cutter.lisA_LastKnownChange)}
				value={lisaLastUpdate}
				label="Information Date" />
		</Grid>),
		(<Grid key='dashboard' >
			<CarouselTextField defaultValue={getDurationStringFromDate(cutter.dasH_LastEventDate)} label={<b>Dashboard</b>} />
			<CarouselTextField defaultValue={getFormatedStringFromDate(cutter.dasH_LastEventDate)} label="Latest Event" />
			<CarouselTextField defaultValue={getFormatedStringFromDate(cutter.dasH_LastKnownChange)} label="Information Date" />
		</Grid>),
		(<Grid key='software'>
			<CarouselTextField defaultValue={cutter.sofT_Pilot_Version} label={<b>Pilot</b>} />
			<CarouselTextField defaultValue={cutter.sofT_CMachine_Version} label="CMachine" />
			<CarouselTextField defaultValue={cutter.sofT_Events_Manager_Version} label="EventManager" />
		</Grid>),
		(<Grid key='hardware'>
			<CarouselTextField defaultValue={cutter.harD_CUT_Machine} label={<b>Pilot/CMachine</b>} />
			<CarouselTextField defaultValue={cutter.harD_Bibliobus_Machine} label="Bibliobus" />
			<CarouselTextField defaultValue={cutter.harD_Events_Manager_Machine} label="EventManager" />
		</Grid>)
	];
	// const carouselRef = useRef<CuttersDetailsCarouselProps>();
	React.useEffect(() => { // reload
		// if (carouselRef.current)carouselRef.current.children.RemoveAt(carouselRef.current.children.length - 1);
	}, []); // Added empty dependency array

	if (cutter.harD_Model === 'Versalis') {
		children = children.concat(<Grid key='softwareVersalis1'>
			<CarouselTextField defaultValue={cutter.sofT_VERS_LSVVersion} label={<b>Leather Supervisor</b>} />
			<CarouselTextField defaultValue={cutter.sofT_VERS_LNestVersion} label="Leather Nest" />
			<CarouselTextField defaultValue={cutter.sofT_VERS_LScanVersion} label="Leather Scan" />
		</Grid>, <Grid key='softwareVersalis2'>
			<CarouselTextField defaultValue={cutter.sofT_VERS_LDBSrvVersion} label={<b>Leather Database</b>} />
			<CarouselTextField defaultValue={cutter.sofT_VERS_OffloadVersion} label="Leather Offload" />
			<CarouselTextField defaultValue={cutter.sofT_VERS_LCMachineVersion} label="Leather CMachine" />
		</Grid>);
	}


	return (
			<Carousel {...others} animation='slide' duration={100}  >
				{children}
			</Carousel>
	)
}


